import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, CalendarClock, ClipboardList, Loader2, Crown, Shield, Briefcase,
  Check, X, Wallet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ROLE_META = {
  owner: { label: 'Īpašnieks', icon: Crown },
  co_owner: { label: 'Līdzīpašnieks', icon: Shield },
  employee: { label: 'Darbinieks', icon: Briefcase },
};
const STATUS_LABEL = { confirmed: 'Apstiprināts', ready: 'Gatavs', completed: 'Izsniegts', cancelled: 'Atcelts', rejected: 'Noraidīts', pending: 'Jauns' };
const STATUS_PILL = { confirmed: 'bg-emerald-500/20 text-emerald-300', ready: 'bg-amber-500/20 text-amber-300', completed: 'bg-sky-500/20 text-sky-300', cancelled: 'bg-white/10 text-white/40', rejected: 'bg-white/10 text-white/40' };

const mondayOf = (iso) => { const d = new Date(iso + 'T00:00:00'); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return d.toISOString().slice(0, 10); };

export default function StaffBusinessPage() {
  const { bizId, pageId: pageIdParam } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [pageId, setPageId] = useState(pageIdParam || null);
  const [bizName, setBizName] = useState('');
  const [access, setAccess] = useState('loading'); // loading | ok | denied
  const [bookings, setBookings] = useState([]);
  const [ordersTab, setOrdersTab] = useState('new');
  const [calMode, setCalMode] = useState('day');
  const [dayDate, setDayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dayView, setDayView] = useState(null);
  const [weekView, setWeekView] = useState(null);
  const [onsite, setOnsite] = useState(null); // booking being marked
  const [onsiteAmt, setOnsiteAmt] = useState('');
  const [onsiteMethod, setOnsiteMethod] = useState('onsite');
  const [onsiteBusy, setOnsiteBusy] = useState(false);
  const todayIso = new Date().toISOString().slice(0, 10);

  const loadBookings = useCallback(async (pid) => {
    try {
      const { data } = await axios.get(`${API}/business-page/bookings/my`, { params: { biz: bizId, page_id: pid } });
      setBookings(data.bookings || []);
    } catch (e) { /* ignore */ }
  }, [bizId]);

  const loadDay = useCallback(async (d, pid) => {
    try { const { data } = await axios.get(`${API}/business-page/bookings/day`, { params: { biz: bizId, page_id: pid, date: d } }); setDayView(data); }
    catch (e) { setDayView(null); }
  }, [bizId]);

  const loadWeek = useCallback(async (d, pid) => {
    try { const { data } = await axios.get(`${API}/business-page/bookings/week`, { params: { biz: bizId, page_id: pid, start: mondayOf(d) } }); setWeekView(data); }
    catch (e) { setWeekView(null); }
  }, [bizId]);

  useEffect(() => {
    (async () => {
      try {
        const mine = await axios.get(`${API}/business-team/mine`).then((r) => r.data).catch(() => ({ pages: [] }));
        const rows = (mine.pages || []).filter((b) => b.business_user_id === bizId);
        // When the URL names a page, it MUST be one shared with me — otherwise deny
        // (strict isolation: never silently fall back to another page).
        const row = pageIdParam ? rows.find((b) => b.page_id === pageIdParam) : rows[0];
        if (!row) { setAccess('denied'); return; }
        setRole(row.role || 'employee');
        setPageId(row.page_id);
        setBizName(`${row.name} · ${row.page_label}`);
        setAccess('ok');
        loadBookings(row.page_id);
      } catch (e) {
        setAccess('denied');
      }
    })();
  }, [bizId, pageIdParam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (access === 'ok' && pageId) { if (calMode === 'day') loadDay(dayDate, pageId); else loadWeek(dayDate, pageId); } }, [access, calMode, dayDate, pageId, loadDay, loadWeek]);

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/business-page/bookings/${id}/status`, { status });
      toast.success('Atjaunināts');
      loadBookings(pageId);
      if (calMode === 'day') loadDay(dayDate, pageId); else loadWeek(dayDate, pageId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās'); }
  };

  const submitOnsite = async () => {
    const amt = Number(onsiteAmt);
    if (!(amt >= 0)) { toast.error('Ievadi summu'); return; }
    setOnsiteBusy(true);
    try {
      await axios.post(`${API}/business-page/bookings/${onsite.id}/mark-onsite`, { amount: amt, method: onsiteMethod });
      toast.success('Atzīmēts kā apmaksāts klātienē');
      setOnsite(null); setOnsiteAmt('');
      loadBookings(pageId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās'); }
    finally { setOnsiteBusy(false); }
  };

  const shiftDay = (n) => { const d = new Date(dayDate + 'T00:00:00'); d.setDate(d.getDate() + (calMode === 'week' ? n * 7 : n)); setDayDate(d.toISOString().slice(0, 10)); };

  const filtered = bookings.filter((b) => {
    const isOrd = b.kind === 'order';
    if (ordersTab === 'all') return true;
    if (ordersTab === 'new') return b.status === 'pending';
    if (ordersTab === 'processing') return ['confirmed', 'ready'].includes(b.status) && (isOrd || (b.date || '') >= todayIso);
    if (ordersTab === 'done') return b.status === 'completed' || (b.status === 'confirmed' && !isOrd && (b.date || '') < todayIso);
    if (ordersTab === 'cancelled') return b.status === 'cancelled' || b.status === 'rejected';
    return true;
  });

  const RM = ROLE_META[role] || ROLE_META.employee;
  const RoleIcon = RM.icon;
  const dayTotal = dayView ? (dayView.slots || []).reduce((n, s) => n + s.bookings.length, 0) : 0;

  if (access === 'loading') return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  if (access === 'denied') return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-center px-6" data-testid="staff-denied">
      <p className="text-white text-lg mb-2">Nav piekļuves</p>
      <p className="text-white/50 text-sm mb-6">Tev nav tiesību pārvaldīt šī uzņēmuma rezervācijas.</p>
      <button onClick={() => navigate('/business-templates')} className="text-sm font-semibold px-5 py-2.5 rounded-full bg-[var(--accent,#FF9F1C)] text-black">Atpakaļ</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]" style={{ '--accent': '#FF9F1C', '--accent-soft': 'rgba(255,159,28,0.12)', '--accent-border': 'rgba(255,159,28,0.35)', '--accent-contrast': '#1a1206', fontFamily: 'Manrope, sans-serif' }} data-testid="staff-business-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 pb-28">
        <button onClick={() => navigate('/business-templates')} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-5 transition-colors" data-testid="staff-back-btn">
          <ArrowLeft className="w-4 h-4" /> Mans bizness
        </button>
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="staff-biz-name">{bizName}</h1>
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/10 text-white/70" data-testid="staff-role-badge"><RoleIcon className="w-3.5 h-3.5" /> {RM.label}</span>
        </div>
        <p className="text-white/50 text-sm mb-7">Kopīgota komanda · tu vari pārvaldīt rezervācijas un pieprasījumus.</p>

        {/* Orders list */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
          {[{ id: 'new', label: 'Jauni' }, { id: 'processing', label: 'Apstrādē' }, { id: 'done', label: 'Izpildīti' }, { id: 'cancelled', label: 'Atcelti' }, { id: 'all', label: 'Visi' }].map((t) => {
            const on = ordersTab === t.id;
            const cnt = t.id === 'new' ? bookings.filter((b) => b.status === 'pending').length : 0;
            return (
              <button key={t.id} onClick={() => setOrdersTab(t.id)}
                className={`shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full border transition-colors ${on ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'bg-white/5 text-white/60 border-white/10 hover:text-white'}`}
                data-testid={`staff-orders-tab-${t.id}`}>
                {t.label}{cnt > 0 && <span className={`text-[10px] font-bold rounded-full px-1.5 leading-none ${on ? 'bg-black/20' : 'bg-[var(--accent)] text-[var(--accent-contrast)]'}`}>{cnt}</span>}
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-10 text-center mb-8" data-testid="staff-orders-empty">
            <ClipboardList className="w-7 h-7 mx-auto mb-2 text-white/20" />
            <p className="text-sm text-white/40">Šajā sadaļā nav rezervāciju.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden divide-y divide-white/5 mb-8" data-testid="staff-orders-list">
            {filtered.map((b) => {
              const unpaid = b.payment_status !== 'paid' && Number(b.amount || 0) > 0;
              return (
                <div key={b.id} className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap" data-testid={`staff-order-row-${b.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{b.name || 'Klients'}{b.service_name ? ` · ${b.service_name}` : ''}{b.party_size ? ` · ${b.party_size} pers.` : ''}</p>
                    <p className="text-xs text-white/50">
                      {b.kind === 'order' ? <>{b.fulfillment === 'delivery' ? '🚚 Piegāde' : '🏪 Uz vietas'}{b.requested_time ? ` · ${b.requested_time}` : ''}</> : <>{b.date || '—'} {b.time_slot || ''}</>}
                      {b.phone ? ` · ${b.phone}` : ''}{b.code ? <span className="ml-1 font-mono text-[var(--accent)]">Kods: {b.code}</span> : null}
                      {Number(b.amount || 0) > 0 && <span className="ml-1">· €{Number(b.amount).toFixed(2)} {b.payment_status === 'paid' ? '✓' : ''}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    {b.status === 'pending' ? (
                      <>
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[var(--accent-soft)] text-[var(--accent)]" data-testid={`staff-order-status-${b.id}`}>Gaida</span>
                        <button onClick={() => setStatus(b.id, 'confirmed')} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors" data-testid={`staff-approve-${b.id}`}>Apstiprināt</button>
                        <button onClick={() => setStatus(b.id, 'rejected')} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 text-white/70 hover:bg-white/20 transition-colors" data-testid={`staff-reject-${b.id}`}>Noraidīt</button>
                      </>
                    ) : (
                      <>
                        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_PILL[b.status] || 'bg-white/10 text-white/40'}`} data-testid={`staff-order-status-${b.id}`}>{STATUS_LABEL[b.status] || b.status}</span>
                        {['confirmed', 'ready'].includes(b.status) && unpaid && (
                          <button onClick={() => { setOnsite(b); setOnsiteAmt(String(Number(b.amount || 0).toFixed(2))); }}
                            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-sky-500/90 text-white hover:bg-sky-500 transition-colors" data-testid={`staff-onsite-${b.id}`}>
                            <Wallet className="w-3.5 h-3.5" /> Apmaksāts klātienē
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Calendar */}
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="staff-calendar">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[var(--accent)]" />
              <p className="font-semibold text-white text-sm">{calMode === 'day' ? 'Dienas kalendārs' : 'Nedēļas kalendārs'}</p>
              {calMode === 'day' && dayView ? <span className="text-xs text-white/40">· {dayTotal} rez.</span> : null}
              {calMode === 'week' && weekView ? <span className="text-xs text-white/40">· {weekView.total} rez.</span> : null}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center p-0.5 rounded-full bg-white/10 border border-white/10">
                <button onClick={() => setCalMode('day')} className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${calMode === 'day' ? 'bg-[var(--accent)] text-[var(--accent-contrast)]' : 'text-white/60'}`} data-testid="staff-cal-day">Diena</button>
                <button onClick={() => setCalMode('week')} className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${calMode === 'week' ? 'bg-[var(--accent)] text-[var(--accent-contrast)]' : 'text-white/60'}`} data-testid="staff-cal-week">Nedēļa</button>
              </div>
              <button onClick={() => shiftDay(-1)} className="w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center" data-testid="staff-day-prev">‹</button>
              <input type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)} className="text-sm bg-white/5 border border-white/15 rounded-lg px-3 py-1.5 text-white [color-scheme:dark]" data-testid="staff-day-input" />
              <button onClick={() => shiftDay(1)} className="w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center" data-testid="staff-day-next">›</button>
            </div>
          </div>
          {calMode === 'day' ? (
            <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto">
              {dayView && dayView.slots && dayView.slots.length > 0 ? dayView.slots.map((s) => (
                <div key={s.time_slot} className="px-5 py-2.5 flex gap-4" data-testid={`staff-day-slot-${s.time_slot}`}>
                  <div className="w-14 flex-shrink-0 pt-1"><span className="text-sm font-mono font-semibold text-white/80">{s.time_slot}</span></div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {s.bookings.length === 0 ? <p className="text-xs text-white/25 py-1">Brīvs</p> : s.bookings.map((b) => (
                      <div key={b.id} className="flex items-center justify-between gap-3 flex-wrap rounded-xl bg-white/5 px-3 py-2" data-testid={`staff-day-booking-${b.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{b.name || 'Klients'}{b.service_name ? ` · ${b.service_name}` : ''}{b.party_size ? ` · ${b.party_size} pers.` : ''}</p>
                          <p className="text-xs text-white/40">{b.phone || ''}{b.code ? <span className="ml-2 font-mono text-[var(--accent)]">{b.code}</span> : null}</p>
                        </div>
                        {b.status === 'pending' ? (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={() => setStatus(b.id, 'confirmed')} className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-500 text-white hover:bg-emerald-600" data-testid={`staff-day-approve-${b.id}`}>Apstiprināt</button>
                            <button onClick={() => setStatus(b.id, 'rejected')} className="text-xs font-semibold px-3 py-1 rounded-full bg-white/10 text-white/70 hover:bg-white/20" data-testid={`staff-day-reject-${b.id}`}>Noraidīt</button>
                          </div>
                        ) : (
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${b.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/50'}`}>{STATUS_LABEL[b.status] || b.status}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )) : <p className="px-5 py-6 text-center text-sm text-white/40">Šai dienai nav rezervāciju.</p>}
            </div>
          ) : (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="staff-week-grid">
              {weekView && weekView.days && weekView.days.map((d) => {
                const dt = new Date(d.date + 'T00:00:00');
                const wd = ['Pr', 'Ot', 'Tr', 'Ce', 'Pk', 'Se', 'Sv'][(dt.getDay() + 6) % 7];
                const isToday = d.date === todayIso;
                return (
                  <div key={d.date} className={`rounded-xl border p-2 min-h-[120px] ${isToday ? 'border-[var(--accent-border)] bg-[var(--accent-soft)]' : 'border-white/10 bg-white/5'}`} data-testid={`staff-week-day-${d.date}`}>
                    <button onClick={() => { setCalMode('day'); setDayDate(d.date); }} className="w-full text-left mb-2">
                      <p className="text-[11px] uppercase tracking-wide text-white/40">{wd}</p>
                      <p className="text-sm font-semibold text-white">{d.date.slice(8, 10)}.{d.date.slice(5, 7)}</p>
                    </button>
                    <div className="space-y-1">
                      {d.count === 0 ? <p className="text-[11px] text-white/20">—</p> : d.bookings.slice(0, 4).map((b) => (
                        <div key={b.id} className={`rounded-lg px-2 py-1 text-[11px] leading-tight ${b.status === 'pending' ? 'bg-[var(--accent-soft)] text-[var(--accent)]' : b.status === 'confirmed' ? 'bg-emerald-500/15 text-emerald-200' : 'bg-white/10 text-white/50'}`} data-testid={`staff-week-booking-${b.id}`}>
                          <span className="font-mono">{b.time_slot}</span> <span className="truncate">{b.name}</span>
                        </div>
                      ))}
                      {d.count > 4 && <p className="text-[11px] text-white/40">+{d.count - 4} vēl</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Onsite payment dialog */}
      {onsite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => !onsiteBusy && setOnsite(null)} data-testid="staff-onsite-dialog">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#141414] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-white font-semibold">Apmaksāts klātienē</p>
              <button onClick={() => setOnsite(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-white/50 mb-4">{onsite.name || 'Klients'}{onsite.service_name ? ` · ${onsite.service_name}` : ''}</p>
            <label className="block text-xs text-white/50 mb-1">Summa (€)</label>
            <input type="number" min="0" step="0.01" value={onsiteAmt} onChange={(e) => setOnsiteAmt(e.target.value)}
              className="w-full h-11 rounded-xl bg-white/5 border border-white/10 text-white text-sm px-3 mb-4 [color-scheme:dark]" data-testid="staff-onsite-amount" />
            <button onClick={submitOnsite} disabled={onsiteBusy}
              className="w-full h-11 rounded-xl bg-[var(--accent)] text-[var(--accent-contrast)] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="staff-onsite-confirm">
              {onsiteBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} Apstiprināt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
